import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { CollateralNotes } from "@app/models/collateral-notes";
import { CollateralNotesService } from "@app/services/collateral-notes.service";

@Component({
    selector: "app-select-collateral-note-popup",
    templateUrl: "./select-collateral-note-popup.component.html",
    styleUrls: ["./select-collateral-note-popup.component.css"],
    standalone: false
})
export class SelectCollateralNotePopupComponent implements OnInit {
  @Input() collateralNotes: Array<CollateralNotes>;
  @Input() displaySearchDialog: boolean;
  @Input() selectedCollateralNotes: Array<CollateralNotes>;
  @Output() onSubmit = new EventEmitter<Array<CollateralNotes>>();
  @Output() onCancel = new EventEmitter();

  constructor(private collateralNotesService: CollateralNotesService) {}

  ngOnInit() {
    if (!this.selectedCollateralNotes) {
      this.selectedCollateralNotes = [];
    } else {
      this.selectedCollateralNotes = [...this.selectedCollateralNotes];
    }
    this.addMandatoryNotes();
    this.syncInitialSelection();
  }

  private addMandatoryNotes() {
    for (const root of this.collateralNotes || []) for (const note of this.findAllChidren(root)) if (note.mandatory && !this.selectedCollateralNotes.some((selected) => selected.id === note.id)) this.selectedCollateralNotes.push(note);
  }

  syncInitialSelection() {
    if (!this.collateralNotes || this.collateralNotes.length === 0) {
      return;
    }

    const currentList = [...this.selectedCollateralNotes];
    for (const note of currentList) {
      const fullNote = this.collateralNotesService.findCollateralNoteById(note.id, this.collateralNotes) || note;
      const allChildren = this.findAllChidren(fullNote);
      for (const child of allChildren) {
        if (!this.selectedCollateralNotes.some((n) => n.id === child.id)) {
          this.selectedCollateralNotes.push(child);
        }
      }
    }

    this.updateAllParentsSelection();
  }

  hideSearch() {
    this.onCancel.emit();
  }

  onSelect(collateralNote: CollateralNotes) {
    if (!collateralNote) {
      return;
    }

    const fullNote = this.collateralNotesService.findCollateralNoteById(collateralNote.id, this.collateralNotes) || collateralNote;
    const allDescendants = this.findAllChidren(fullNote);
    const updated = [...this.selectedCollateralNotes];
    for (const item of allDescendants) {
      if (!updated.some((p) => p.id === item.id)) {
        updated.push(item);
      }
    }
    this.selectedCollateralNotes = updated;

    this.updateAllParentsSelection();
  }

  onSave() {
    this.onSubmit.emit(this.selectedCollateralNotes);
  }

  deleteSelectedCollateralNote(collateralNote: CollateralNotes) {
    if (!collateralNote) {
      return;
    }

    const fullNote = this.collateralNotesService.findCollateralNoteById(collateralNote.id, this.collateralNotes) || collateralNote;
    const allDescendants = this.findAllChidren(fullNote);
    const removeIds = new Set(allDescendants.map((item) => item.id));

    this.selectedCollateralNotes = this.selectedCollateralNotes.filter((item) => !removeIds.has(item.id));
  }

  updateAllParentsSelection() {
    if (!this.collateralNotes || this.collateralNotes.length === 0) {
      return;
    }

    const selectedSet = new Set(this.selectedCollateralNotes.map((n) => n.id));

    const checkNode = (node: CollateralNotes): boolean => {
      if (!node.subCollateralNotes || node.subCollateralNotes.length === 0) {
        return selectedSet.has(node.id);
      }

      let allChildrenSelected = true;
      for (const child of node.subCollateralNotes) {
        const childSelected = checkNode(child);
        if (!childSelected) {
          allChildrenSelected = false;
        }
      }

      if (allChildrenSelected) {
        selectedSet.add(node.id);
      } else {
        selectedSet.delete(node.id);
      }

      return allChildrenSelected;
    };

    for (const rootNode of this.collateralNotes) {
      checkNode(rootNode);
    }

    const newSelected: CollateralNotes[] = [];
    for (const item of this.selectedCollateralNotes) {
      if (selectedSet.has(item.id) && !newSelected.some((n) => n.id === item.id)) {
        newSelected.push(item);
      }
    }
    for (const id of selectedSet) {
      if (!newSelected.some((n) => n.id === id)) {
        const found = this.collateralNotesService.findCollateralNoteById(id, this.collateralNotes);
        if (found) {
          newSelected.push(found);
        }
      }
    }

    this.selectedCollateralNotes = newSelected;
  }

  findAllChidren(collateralNote: CollateralNotes): CollateralNotes[] {
    let items: CollateralNotes[] = [];
    if (collateralNote) {
      items.push(collateralNote);
      if (collateralNote.subCollateralNotes && collateralNote.subCollateralNotes.length > 0) {
        collateralNote.subCollateralNotes.forEach((p) => {
          items = items.concat(this.findAllChidren(p));
        });
      }
    }
    return items;
  }
}
